/**
 * Demande de publication d'une formation sur skills4mation.com (31/10/2026).
 *
 * La vitrine (`vitrine/`) n'a pas de base de données : chaque fiche est un fichier JSON versionné
 * (`vitrine/src/data/catalogue/fiches/<slug>.json`), validé au build par `schemaFiche` (`vitrine/src/lib/catalogue.ts`,
 * objet strict : un champ inconnu est refusé). Ce module produit, à partir d'une formation du SaaS, un objet AU MÊME
 * FORMAT, champ pour champ ; la publication effective reste un commit dans `vitrine/` (manuel).
 *
 * Module pur : aucun accès réseau ni base. Testé par `fiche-vitrine.test.ts`, qui confronte la sortie au schéma réel
 * de la vitrine (lu dans le dépôt).
 */

/** Catégories du catalogue — copie de `vitrine/src/lib/categories.ts` (le test vérifie qu'elles sont identiques). */
export const CATEGORIES_VITRINE = [
  { slug: "bien-etre", label: "Bien-être" },
  { slug: "bureautique", label: "Bureautique & digital" },
  { slug: "communication", label: "Communication" },
  { slug: "creation-dentreprise", label: "Business & création d’entreprise" },
  { slug: "langues", label: "Langues" },
  { slug: "metier-specifique", label: "Métier spécifique" },
  { slug: "rh-management", label: "RH & Management" },
  { slug: "rse", label: "RSE" },
  { slug: "ventes", label: "Ventes" },
] as const;
export type CategorieVitrine = (typeof CATEGORIES_VITRINE)[number]["slug"];
export const estCategorieVitrine = (x: string): x is CategorieVitrine => CATEGORIES_VITRINE.some((c) => c.slug === x);

/** Ordre des champs d'une fiche de la vitrine (celui de `schemaFiche`). */
export const CHAMPS_FICHE = ["slug", "titre", "titreSeo", "heading", "categorie", "image", "imageAlt", "intro", "niveau", "tarif", "objectif", "duree", "prerequis", "objectifs", "public", "modalites", "forts", "resultats", "programme", "anciens", "modalitesEvaluation", "delaisAcces", "accessibiliteHandicap", "derniereMiseAJour", "certification", "ambassadeur", "formateur", "miseAJour"] as const;

export interface FicheVitrine {
  slug: string;
  titre: string;
  titreSeo?: string;
  heading: string;
  categorie: CategorieVitrine;
  image: string | null;
  imageAlt?: string;
  intro: string;
  niveau: string;
  tarif: string;
  objectif: string;
  duree: string;
  prerequis: string;
  objectifs: string[];
  public: string[];
  modalites: string[];
  forts: string[];
  resultats: string[];
  programme: Array<{ titre: string; points: string[] }>;
  anciens: never[];
  modalitesEvaluation?: string[];
  delaisAcces?: string;
  accessibiliteHandicap?: string;
  /** Programme ambassadeur (04/11/2026) : les demandes reçues via la fiche sont relayées au formateur (formulaire affiché). */
  ambassadeur?: true;
  /** Nom et photo du formateur, avec son consentement (droit à l'image). `photo` : fichier de `public/`, ou null. */
  formateur?: { nom: string; photo: string | null };
  miseAJour: string;
}

export const REGEX_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** Chemin d'image accepté par la vitrine : un fichier de `public/`. */
export const REGEX_IMAGE = /^\/(?!\/)\S+\.(?:png|jpe?g|webp|gif|avif)$/i;

/** « Formation Gestion du temps » → `formation-gestion-du-temps` (préfixe « formation- » comme les fiches existantes). */
export function slugDepuisTitre(titre: string): string {
  const base = titre
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 90)
    .replace(/-+$/g, "");
  if (!base) return "formation";
  return base.startsWith("formation") ? base : `formation-${base}`;
}

const lignes = (texte: string | null | undefined): string[] =>
  (texte ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim().replace(/^[-•*]\s*/, ""))
    .filter(Boolean);

const heures = (h: number) => `${String(h).replace(".", ",")} h`;

/** Balise <title> : la vitrine calcule « Formation <titre> | Skills4mation » ; au-delà de 60 caractères, `titreSeo` (10 à 60). */
export function titreSeoSiNecessaire(titre: string): string | undefined {
  const calcule = `${/^formation\b/i.test(titre) ? titre : `Formation ${titre}`} | Skills4mation`;
  if (calcule.length <= 60) return undefined;
  const court = titre.length <= 60 ? titre : `${titre.slice(0, 59).replace(/\s+\S*$/, "")}…`;
  return court.length >= 10 ? court : undefined;
}

export interface FormationSource {
  formation_titre: string;
  formation_objectifs: string;
  formation_niveau: string;
  formation_prerequis: string;
  formation_duree_heures_total: number | null;
  formation_duree_jours: number | null;
  formation_modalite: "presentiel" | "distanciel" | "mixte";
  formation_prix_unitaire_ht: number | null;
  programme: string;
  public_vise: string;
  formation_modules: unknown;
  formation_moyens_pedagogiques: string;
  formation_modalites_evaluation: string;
  formation_accessibilite: string;
  formation_delai_acces: string;
}

interface ModuleSource {
  titre?: unknown;
  duree_heures?: unknown;
  contenus?: unknown;
  objectifs?: unknown;
}

const MODALITES: Record<FormationSource["formation_modalite"], string> = {
  presentiel: "Formation en présentiel avec un formateur dédié.",
  distanciel: "Formation à distance, en classe virtuelle, avec un formateur dédié.",
  mixte: "Formation mixte : séances en présentiel et à distance, avec un formateur dédié.",
};

const MODALITE_COURTE: Record<FormationSource["formation_modalite"], string> = { presentiel: "en présentiel", distanciel: "à distance", mixte: "en mixte (présentiel et distanciel)" };

export function tarifTexte(centimes: number | null): string {
  if (centimes == null || centimes <= 0) return "Sur devis - Financement possible";
  const e = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(centimes / 100)).replace(/\u202f|\u00a0/g, " ");
  return `A partir de ${e} € HT - Financement possible`;
}

export function dureeTexte(h: number | null, j: number | null): string {
  if (!h) return "A déterminer selon votre niveau et objectif";
  return j ? `${heures(h)} (${String(j).replace(".", ",")} jour${j > 1 ? "s" : ""})` : heures(h);
}

/** Le programme de la fiche : un bloc par module du parcours ; à défaut, le programme rédigé, en un bloc. */
export function programmeFiche(f: Pick<FormationSource, "formation_modules" | "programme">): Array<{ titre: string; points: string[] }> {
  const modules = (Array.isArray(f.formation_modules) ? f.formation_modules : []) as ModuleSource[];
  const blocs = modules
    .map((m, i) => {
      const titre = typeof m.titre === "string" ? m.titre.trim() : "";
      const duree = typeof m.duree_heures === "number" && m.duree_heures > 0 ? ` (${heures(m.duree_heures)})` : "";
      const points = (Array.isArray(m.contenus) ? m.contenus : []).filter((x): x is string => typeof x === "string" && x.trim() !== "").map((x) => x.trim());
      return titre ? { titre: `Module ${i + 1} — ${titre}${duree}`, points } : null;
    })
    .filter((x): x is { titre: string; points: string[] } => x !== null);
  if (blocs.length) return blocs;
  const p = lignes(f.programme);
  return p.length ? [{ titre: "Programme", points: p }] : [];
}

export interface OptionsFiche {
  slug: string;
  categorie: CategorieVitrine;
  /** AAAA-MM-JJ — date de la fiche (`miseAJour`, reprise dans le sitemap). */
  date: string;
  /** Chemin dans `vitrine/public` (« /images/formations/<slug>.webp »), ou null sans visuel. */
  image: string | null;
  imageAlt?: string;
}

/** Fiche au format exact de la vitrine. Les champs facultatifs vides sont omis (le schéma les refuserait vides : `titreSeo`, `imageAlt`). */
export function ficheVitrineDepuisFormation(f: FormationSource, o: OptionsFiche): FicheVitrine {
  const titre = f.formation_titre.trim();
  const objectifs = lignes(f.formation_objectifs);
  const publicVise = lignes(f.public_vise);
  const intro = [
    `${titre} : ${f.formation_duree_heures_total ? `${heures(f.formation_duree_heures_total)} de formation` : "une formation"} ${MODALITE_COURTE[f.formation_modalite]}, avec un formateur dédié.`,
    objectifs.length ? `Objectif : ${objectifs[0]!.replace(/[.;:]+$/, "")}.` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const titreSeo = titreSeoSiNecessaire(titre);
  const alt = o.image && o.imageAlt?.trim() ? o.imageAlt.trim().slice(0, 200) : undefined;
  const evaluation = lignes(f.formation_modalites_evaluation);
  const fiche: FicheVitrine = {
    slug: o.slug,
    titre,
    ...(titreSeo ? { titreSeo } : {}),
    heading: titre,
    categorie: o.categorie,
    image: o.image,
    ...(alt && alt.length >= 10 ? { imageAlt: alt } : {}),
    intro,
    niveau: f.formation_niveau.trim() || "Tous niveaux",
    tarif: tarifTexte(f.formation_prix_unitaire_ht),
    objectif: objectifs[0] ?? "",
    duree: dureeTexte(f.formation_duree_heures_total, f.formation_duree_jours),
    prerequis: f.formation_prerequis.trim() || "Aucun",
    objectifs,
    public: publicVise,
    modalites: [MODALITES[f.formation_modalite], ...lignes(f.formation_moyens_pedagogiques)],
    forts: [],
    resultats: [],
    programme: programmeFiche(f),
    anciens: [],
    ...(evaluation.length ? { modalitesEvaluation: evaluation } : {}),
    ...(f.formation_delai_acces.trim() ? { delaisAcces: f.formation_delai_acces.trim() } : {}),
    ...(f.formation_accessibilite.trim() ? { accessibiliteHandicap: f.formation_accessibilite.trim() } : {}),
    miseAJour: o.date,
  };
  return fiche;
}

/** Remet les champs dans l'ordre de la vitrine (`CHAMPS_FICHE`) : le fichier JSON reste lisible et stable. */
export function ordonnerFiche(f: FicheVitrine): FicheVitrine {
  const o = f as unknown as Record<string, unknown>;
  return Object.fromEntries(CHAMPS_FICHE.filter((c) => o[c] !== undefined).map((c) => [c, o[c]])) as unknown as FicheVitrine;
}

/** Contrôles du schéma de la vitrine repris ici (la vitrine refuserait le fichier au build). Renvoie la liste des erreurs. */
export function verifierFiche(f: FicheVitrine): string[] {
  const e: string[] = [];
  if (!REGEX_SLUG.test(f.slug)) e.push("Slug : minuscules, chiffres et tirets.");
  if (!f.titre) e.push("Titre manquant.");
  if (f.titreSeo !== undefined && (f.titreSeo.length < 10 || f.titreSeo.length > 60)) e.push("Titre SEO : 10 à 60 caractères.");
  if (!estCategorieVitrine(f.categorie)) e.push("Catégorie inconnue de la vitrine.");
  if (f.image !== null && !REGEX_IMAGE.test(f.image)) e.push("Image : chemin d'un fichier de public/.");
  if (f.imageAlt !== undefined && (f.imageAlt.length < 10 || f.imageAlt.length > 200)) e.push("Texte alternatif : 10 à 200 caractères.");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.miseAJour)) e.push("Date de mise à jour invalide.");
  if (f.formateur !== undefined && (!f.formateur.nom.trim() || f.formateur.nom.length > 120)) e.push("Nom du formateur : 1 à 120 caractères.");
  if (f.formateur?.photo && !REGEX_IMAGE.test(f.formateur.photo)) e.push("Photo du formateur : chemin d'un fichier de public/.");
  return e;
}

/** Les trois largeurs publiées par la vitrine (`visuels.json`) : `<slug>.webp` (1200), `<slug>-800.webp`, `<slug>-400.webp`. */
export const LARGEURS_VISUEL = [1200, 800, 400] as const;
export const cheminVisuelVitrine = (slug: string) => `/images/formations/${slug}.webp`;
export const nomFichierVisuel = (slug: string, largeur: (typeof LARGEURS_VISUEL)[number]) => (largeur === 1200 ? `${slug}.webp` : `${slug}-${largeur}.webp`);

/**
 * Consigne de génération d'image (IA) : dans le style des visuels de la vitrine — photo réaliste, lumière naturelle —
 * sans texte, sans logo, sans personne célèbre ni visage reconnaissable d'une personne réelle.
 */
export function consigneVisuel(f: { titre: string; domaine?: string; public_vise?: string }): string {
  const publicVise = lignes(f.public_vise).slice(0, 2).join(", ");
  return [
    `Photographie réaliste, format paysage 3:2, pour illustrer une formation professionnelle intitulée « ${f.titre.trim()} »${f.domaine?.trim() ? ` (domaine : ${f.domaine.trim()})` : ""}.`,
    publicVise ? `Public : ${publicVise}.` : "",
    "Scène de formation ou de mise en pratique du métier, dans un cadre professionnel lumineux et contemporain, lumière naturelle douce, couleurs naturelles légèrement chaudes, faible profondeur de champ, cadrage soigné laissant de l'air autour du sujet.",
    "Personnes anonymes et génériques, vues de trois quarts ou de dos, aucun visage reconnaissable, aucune personne célèbre ou réelle.",
    "Aucun texte incrusté, aucune lettre, aucun logo, aucune marque, aucun filigrane, pas d'illustration ni de rendu 3D.",
  ]
    .filter(Boolean)
    .join(" ");
}

/** Texte alternatif proposé pour un visuel généré (10 à 200 caractères) — à relire. */
export function altPropose(titre: string): string {
  return `Photo d'illustration de la formation ${titre.trim()}`.slice(0, 200);
}

export const ESSAIS_IA_MAX = 3;
